import { Text, IconButton, Icon, Tooltip, Box } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CellValue } from 'react-table';

import { IconView } from 'assets/icons';
import SearchField from 'components/SearchField';
import Table from 'components/Table';
import { routePaths } from 'global/routePaths';
import {
  useFetchPaginatedNotification,
  INotificationSearchQuery,
} from 'hooks/notification/useFetchPaginatedNotification';
import { formatYearMonthDayTime } from 'utils/format';

const initialNotificationSearchQuery: INotificationSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const NotificationList: React.FC<unknown> = () => {
  const { t } = useTranslation(['common']);

  const [searchQuery, setSearchQuery] = useState<INotificationSearchQuery>(initialNotificationSearchQuery);

  const navigate = useNavigate();

  const { status, data } = useFetchPaginatedNotification(searchQuery);

  const columns = useMemo(
    () => [
      {
        Header: t('common:notification'),
        accessor: 'message',
        width: 340,
      },
      {
        Header: t('common:from'),
        accessor: 'entity',
        width: 200,
      },
      {
        Header: t('common:status'),
        accessor: 'is_read',
        width: 100,
        Cell: (data: CellValue) => {
          return data.value ? (
            <Text color="success.500" fontWeight="semibold">
              {t('common:read')}
            </Text>
          ) : (
            <Text color="secondary.700" fontWeight="semibold">
              {t('common:unread')}
            </Text>
          );
        },
      },
      {
        Header: t('common:date'),
        accessor: 'created_at',
        width: 200,
        Cell: (data: CellValue) => {
          return <>{data.value ? formatYearMonthDayTime(`${data.value}Z`) : '-'}</>;
        },
      },
      {
        Header: '',
        accessor: 'action',
        width: 100,
        Cell: (data: CellValue) => {
          return (
            <div>
              <Tooltip label={t('common:viewNotification')} hasArrow bg="black">
                <IconButton
                  aria-label="Edit"
                  bg="rgba(38, 103, 201, 0.1)"
                  icon={<Icon as={IconView} sx={{ '& path': { fill: 'primary.600' } }} color="primary.600" />}
                  size="sm"
                  mr="3"
                  _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                  onClick={() => navigate(routePaths.notification.detail.replace(':id', data.row.original.id))}
                />
              </Tooltip>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updateSearchQuery = (previousSearchQuery: INotificationSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updateSearchQuery);
  }, []);

  return (
    <div>
      <Box ml="auto" maxWidth={{ md: '350px' }} width="100%">
        <SearchField inputProps={{ placeholder: t('common:searchNotification'), onChange: handleSearchInputChange }} />
      </Box>

      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination}
        isLoading={status === 'loading'}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default NotificationList;
